import React, { useState, useEffect, useMemo } from 'react';
import { useStore } from '../store/StoreContext';
import { useNav } from '../store/StoreContext';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/useToast';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { IconPlus, IconX, IconChevronLeft, IconTrash, IconEdit, IconChevronRight, IconUsers } from '../components/icons';
import ClientIntakeModal from '../components/modals/ClientIntakeModal';
import { DatePicker, ChoiceKeys, Button, listPanelCls, listRowCls, listRowSelectedCls, listGroupCls, PageHeader, Toolbar, SearchInput, FilterChips, EmptyState, pageWrapCls } from '../components/ui';
import StatusDropdown from '../components/ui/StatusDropdown';
import PriorityPicker from '../components/ui/PriorityPicker';
import { getClientStatuses, getEstimateStatuses, getOperationsStatuses, getStatusLabel, statusTextColor } from '../lib/constants';
import { groupClientsForOrder, recentlyCompletedWalkthroughs, completedStamp, RECENT_WT_DAYS } from '../lib/clientOrder';
import { thumbProps } from '../lib/imageUtils';
import { formatPhone, phoneDigits } from '../lib/phone';
import { useConfirm } from '../hooks/useConfirm';
// Moves (2026-09-18): list rows read the move date through the client's
// CURRENT MOVE and print it with the tentative marker (~Sep 20).
import { currentMoveFor, fmtMoveDate, movesForClient, jobTypeShort, MOVE_TERMINAL } from '../lib/moves';
import { pickPrimaryEstimate } from '../lib/leadPipeline';

// Used by PhotoGallery (the batch grouping + lightbox surfaces below) to
// render source labels. The room-tag list moved into the shared PhotoUpload
// component when we deleted the duplicate local copy that used to live
// here.
const PHOTO_SOURCES = [
    { value: 'office', label: 'Office Upload' },
    { value: 'client', label: 'Client Submitted' },
    { value: 'estimator', label: 'Estimator Walkthrough' },
    { value: 'crew', label: 'Crew / Move Day' },
];

// Read room tags off a photo regardless of which column shape is set.
// New rows write to room_tags (text[]); legacy rows have a single string in
// room_tag. Returns a fresh array.
const photoTags = (p) => {
    if (Array.isArray(p?.room_tags) && p.room_tags.length) return p.room_tags;
    if (p?.room_tag) return [p.room_tag];
    return [];
};

function PhotoGallery({ photos, onDelete }) {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const [lightbox, setLightbox] = useState(null);
    const [filter, setFilter] = useState('all');
    const [collapsedBatches, setCollapsedBatches] = useState({});

    const filtered = filter === 'all' ? photos : photos.filter(p => photoTags(p).includes(filter) || p.source === filter);
    const roomsUsed = [...new Set(photos.flatMap(photoTags))];

    const batches = useMemo(() => {
        const groups = {};
        filtered.forEach(photo => {
            const key = photo.upload_batch_id || `legacy_${(photo.uploaded_at || photo.created_at || '').slice(0, 10) || 'unknown'}`;
            if (!groups[key]) groups[key] = { id: key, photos: [], label: '', earliest: null };
            groups[key].photos.push(photo);
            if (photo.batch_label && !groups[key].label) groups[key].label = photo.batch_label;
            const ts = photo.uploaded_at || photo.created_at;
            if (ts && (!groups[key].earliest || ts < groups[key].earliest)) groups[key].earliest = ts;
        });
        return Object.values(groups).sort((a, b) => (b.earliest || '').localeCompare(a.earliest || ''));
    }, [filtered]);

    const toggleBatch = (batchId) => setCollapsedBatches(prev => ({ ...prev, [batchId]: !prev[batchId] }));

    const formatBatchDate = (ts) => {
        if (!ts) return 'Unknown date';
        const d = new Date(ts);
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    const allVisible = batches.flatMap(b => collapsedBatches[b.id] ? [] : b.photos);

    const lightboxIndex = lightbox ? allVisible.findIndex(p => p.id === lightbox.id) : -1;
    const goPrev = (e) => {
        if (e) e.stopPropagation();
        if (lightboxIndex <= 0 || allVisible.length === 0) return;
        setLightbox(allVisible[lightboxIndex - 1]);
    };
    const goNext = (e) => {
        if (e) e.stopPropagation();
        if (lightboxIndex < 0 || lightboxIndex >= allVisible.length - 1) return;
        setLightbox(allVisible[lightboxIndex + 1]);
    };

    useEffect(() => {
        if (!lightbox) return;
        const handler = (e) => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'Escape') setLightbox(null);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [lightbox, lightboxIndex, allVisible]);

    if (photos.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No photos yet. Upload images above to build the visual inventory.</p>;

    return (
        <div>
            {(roomsUsed.length > 0 || photos.length > 3) && (
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'all' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All ({photos.length})</button>
                    {roomsUsed.map(room => (
                        <button key={room} onClick={() => setFilter(room)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === room ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{room} ({photos.filter(p => photoTags(p).includes(room)).length})</button>
                    ))}
                </div>
            )}

            <div className="space-y-4">
                {batches.map(batch => {
                    const isCollapsed = collapsedBatches[batch.id];
                    const batchTitle = batch.label || formatBatchDate(batch.earliest);
                    const sourceLabel = batch.photos[0]?.source ? (PHOTO_SOURCES.find(s => s.value === batch.photos[0].source) || {}).label : '';
                    return (
                        <div key={batch.id} className="border border-slate-200 rounded-xl overflow-hidden">
                            <div
                                className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                onClick={() => toggleBatch(batch.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className={`text-slate-400 transition-transform text-sm ${isCollapsed ? '' : 'rotate-180'}`}>▾</span>
                                    <div>
                                        <span className="text-sm font-semibold text-slate-800">{batchTitle}</span>
                                        {batch.label && <span className="text-xs text-slate-400 ml-2">{formatBatchDate(batch.earliest)}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {sourceLabel && <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{sourceLabel}</span>}
                                    <span className="bg-slate-200 text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">{batch.photos.length} photo{batch.photos.length !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                            {!isCollapsed && (
                                <div className="grid grid-cols-4 gap-3 p-3">
                                    {batch.photos.map(photo => (
                                        <div key={photo.id} className="group relative rounded-lg overflow-hidden border border-slate-200 hover:border-red-300 cursor-pointer aspect-square bg-slate-100" style={{ contentVisibility: 'auto', containIntrinsicSize: '200px 200px' }} onClick={() => setLightbox(photo)}>
                                            <img {...thumbProps(photo.url, { width: 400, height: 400 })} alt={photo.label || ''} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="absolute bottom-0 left-0 right-0 p-2 flex flex-wrap gap-1">
                                                    {photoTags(photo).map(t => (
                                                        <span key={t} className="px-1.5 py-0.5 bg-white/90 text-slate-700 text-xs rounded font-medium">{t}</span>
                                                    ))}
                                                    <span className="px-1.5 py-0.5 bg-white/90 text-slate-500 text-xs rounded">{(PHOTO_SOURCES.find(s => s.value === photo.source) || {}).label || photo.source}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {lightbox && (
                <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
                    {lightboxIndex > 0 && (
                        <button
                            onClick={goPrev}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-700 p-3 rounded-full shadow-lg z-10"
                            aria-label="Previous photo"
                        ><IconChevronLeft size={24}/></button>
                    )}
                    {lightboxIndex >= 0 && lightboxIndex < allVisible.length - 1 && (
                        <button
                            onClick={goNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-700 p-3 rounded-full shadow-lg z-10"
                            aria-label="Next photo"
                        ><IconChevronRight size={24}/></button>
                    )}
                    <div className="relative max-w-4xl max-h-[90vh] mx-4" onClick={e => e.stopPropagation()}>
                        <img src={lightbox.url} alt={lightbox.label || ''} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
                        <div className="absolute top-2 right-2 flex gap-2">
                            {onDelete && <button className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 shadow-lg" onClick={async () => { if (await confirmDialog({ title: `Delete ${lightbox.label ? `"${lightbox.label}"` : (lightbox.batch_label ? `this ${lightbox.batch_label} photo` : 'this photo')}?`, body: 'The photo is removed from the client file. The rest of the batch and the client record are kept. This can\'t be undone.', confirmLabel: 'Delete photo', danger: true })) { await onDelete(lightbox); setLightbox(null); } }}><IconTrash size={16}/></button>}
                            <button className="bg-white/90 text-slate-700 p-2 rounded-full hover:bg-white shadow-lg" onClick={() => setLightbox(null)}><IconX size={16}/></button>
                        </div>
                        <div className="mt-3 flex items-center gap-3 text-white text-sm">
                            {lightbox.batch_label && <span className="bg-white/20 px-2 py-1 rounded font-medium">{lightbox.batch_label}</span>}
                            {photoTags(lightbox).map(t => (
                                <span key={t} className="bg-white/20 px-2 py-1 rounded">{t}</span>
                            ))}
                            <span className="bg-white/20 px-2 py-1 rounded">{(PHOTO_SOURCES.find(s => s.value === lightbox.source) || {}).label}</span>
                            <span className="text-white/60 text-xs">{lightbox.file_size ? `${(lightbox.file_size / 1024).toFixed(0)} KB` : ''}</span>
                            {allVisible.length > 1 && <span className="text-white/60 text-xs ml-auto">{lightboxIndex + 1} / {allVisible.length}</span>}
                            {lightbox.notes && <span className="text-white/80">{lightbox.notes}</span>}
                        </div>
                    </div>
                </div>
            )}
            {confirmElement}
        </div>
    );
}

function ClientDetailView({ client, onBack, onEdit, onSave, jobs, crew, statuses }) {
    const clientJobs = jobs.filter(j => j.client_id === client.id);
    const [photos, setPhotos] = useState([]);
    const [photosOpen, setPhotosOpen] = useState(true);
    const [form, setForm] = useState({ ...client });
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const { show } = useToast();

    useEffect(() => { setForm({ ...client }); setDirty(false); }, [client?.id]);
    // Re-sync form when client data updates from external sources (e.g. walkthrough app) — but only if user hasn't made local edits
    useEffect(() => { if (!dirty) setForm({ ...client }); }, [client]);

    const handlePhotoUploaded = (photo) => setPhotos(prev => [photo, ...prev]);
    const handlePhotoDelete = async (photo) => { setPhotos(prev => prev.filter(p => p.id !== photo.id)); };

    const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

    const handleSave = async () => {
        setSaving(true);
        const cleaned = { ...form };
        ['move_date', 'walkthrough_date'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null; });
        ['from_sqft', 'from_levels', 'to_sqft', 'to_levels'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = 0; });
        try { await onSave(cleaned); setDirty(false); } catch(e) { show('Save failed: ' + (e.message || 'Unknown error'), 'error'); }
        finally { setSaving(false); }
    };

    const inputCls = 'w-full text-sm text-slate-900 bg-transparent border-0 p-0 mt-0.5 focus:outline-none focus:ring-0 placeholder:text-slate-300';

    const EditField = ({ label, field, type = 'text', placeholder, date = false }) => (
        <div className="py-1.5 border-b border-slate-100 last:border-0">
            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</label>
            {date ? (
                <DatePicker size="sm" className="mt-0.5 w-full" value={form[field] || ''} onChange={v => set(field, v)} placeholder={placeholder || label} ariaLabel={label} />
            ) : (
                <input type={type} className={inputCls} value={form[field] || ''} onChange={e => set(field, e.target.value)} placeholder={placeholder || label} />
            )}
        </div>
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <Button variant="tertiary" size="sm" leftIcon={<IconChevronLeft size={14}/>} onClick={onBack}>Back to list</Button>
                {dirty && (
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
                        <Button variant="secondary" size="sm" onClick={() => { setForm({ ...client }); setDirty(false); }}>Discard</Button>
                        <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>Save Changes</Button>
                    </div>
                )}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl">{(form.name || '?')[0]}</div>
                            <div>
                                <input className="text-2xl font-bold text-slate-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 w-full" value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="Client Name" />
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-sm text-slate-600">{form.status || 'new_lead'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="text-xs font-bold text-slate-500 uppercase mb-3">Contact</h3>
                        <EditField label="Phone" field="phone" placeholder="615-555-0000" />
                        <EditField label="Email" field="email" type="email" placeholder="email@example.com" />
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="text-xs font-bold text-slate-500 uppercase mb-3">Move Details</h3>
                        <EditField label="From" field="origin_address" placeholder="Origin address" />
                        <EditField label="To" field="destination_address" placeholder="Destination address" />
                        <EditField label="Move Date" field="move_date" date />
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="text-xs font-bold text-slate-500 uppercase mb-3">Service</h3>
                        <EditField label="Job Type" field="job_type" placeholder="LOCAL" />
                    </div>
                </div>

                {/* Walkthrough Details Section */}
                {(form.walkthrough_notes || form.special_notes || form.access_notes || form.heavy_items || form.from_stairs || form.to_stairs || form.from_elevator || form.to_elevator || form.packing_needed || form.from_sqft) && (
                    <div className="px-4 md:px-6 pb-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h3 className="text-xs font-bold text-amber-700 uppercase mb-3 flex items-center gap-2">
                                Walkthrough Details
                                <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-medium normal-case">from estimator</span>
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Property Info */}
                                <div className="space-y-1.5">
                                    {form.from_sqft > 0 && <div className="text-sm text-slate-700"><span className="text-slate-400 font-medium">Sq Ft:</span> {form.from_sqft}</div>}
                                    <div className="flex flex-wrap gap-1.5">
                                        {form.from_stairs && <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Pickup: Stairs</span>}
                                        {form.from_elevator && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Pickup: Elevator</span>}
                                        {form.to_stairs && <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Dest: Stairs</span>}
                                        {form.to_elevator && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Dest: Elevator</span>}
                                        {form.packing_needed && <span className="text-[10px] font-bold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Packing Needed</span>}
                                    </div>
                                </div>
                                {/* Heavy Items */}
                                {form.heavy_items && (
                                    <div>
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase">Heavy Items</div>
                                        <p className="text-sm text-slate-700 mt-0.5">{form.heavy_items}</p>
                                    </div>
                                )}
                                {/* Access Notes */}
                                {form.access_notes && (
                                    <div>
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase">Access & Parking</div>
                                        <p className="text-sm text-slate-700 mt-0.5">{form.access_notes}</p>
                                    </div>
                                )}
                            </div>
                            {/* Walkthrough Notes */}
                            {form.walkthrough_notes && (
                                <div className="mt-3 pt-3 border-t border-amber-200">
                                    <div className="text-[10px] font-semibold text-slate-400 uppercase">Walkthrough Notes</div>
                                    <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{form.walkthrough_notes}</p>
                                </div>
                            )}
                            {form.special_notes && (
                                <div className="mt-2">
                                    <div className="text-[10px] font-semibold text-slate-400 uppercase">Special Requirements</div>
                                    <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{form.special_notes}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="px-6 pb-6">
                    <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">Jobs <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{clientJobs.length}</span></h3>
                    <div className="space-y-2">
                        {clientJobs.map(job => {
                            const leader = crew.find(c => c.id === job.leader_id);
                            return (
                                <div key={job.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {leader && <div className="w-2 h-10 rounded-full" style={{backgroundColor: leader.display_color}}></div>}
                                        <div>
                                            <div className="text-sm font-medium text-slate-900">{job.type.replace('_',' ')} • {job.move_date} {job.time}</div>
                                            <div className="text-xs text-slate-600">{job.from_address} → {job.to_address}</div>
                                        </div>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${job.status === 'dispatched' ? 'bg-green-100 text-green-700' : job.status === 'assigned' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{job.status.replace('_',' ')}</span>
                                </div>
                            );
                        })}
                        {clientJobs.length === 0 && <p className="text-sm text-slate-400 py-2">No jobs yet</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function moveDateBadge(moveDate) {
    if (!moveDate) return null;
    try {
        const d = new Date(moveDate + 'T00:00:00');
        const now = new Date();
        const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
        if (diff < 0) return { text: `${Math.abs(diff)}d ago`, cls: 'bg-slate-200 text-slate-600' };
        if (diff === 0) return { text: 'TODAY', cls: 'bg-red-100 text-red-700' };
        if (diff === 1) return { text: 'TOMORROW', cls: 'bg-amber-100 text-amber-700' };
        if (diff <= 7) return { text: `${diff}d away`, cls: 'bg-blue-100 text-blue-700' };
        if (diff <= 14) return { text: `${diff}d away`, cls: 'bg-slate-100 text-slate-600' };
        return null;
    } catch { return null; }
}

function ClientsPage() {
    const { clients, jobs, crew, trucks, estimates, saveClient, saveJob, saveEstimate, config, moves, setCurrentMove } = useStore();
    const { can } = useAuth();
    const canEditClient = can('edit_clients');
    const { openClientId, clearOpenClient, openNewClient, newClientDate, clearOpenNewClient } = useNav();
    // Target move date for a Capacity-page "Book this day" intake.
    const [intakeDate, setIntakeDate] = useState(null);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('smart');
    const [viewMode, setViewMode] = useState('list');
    const { show: showToast } = useToast();

    const statuses = useMemo(() => getClientStatuses(config), [config]);
    const estimateStatuses = useMemo(() => getEstimateStatuses(config), [config]);
    const operationsStatuses = useMemo(() => getOperationsStatuses(config), [config]);
    // Priority chip colors no longer derived here — the PriorityPicker
    // component now handles its own color resolution. Kept this comment
    // so future readers don't re-add the PRIORITY_COLORS lookup.
    const statusCounts = useMemo(() => {
        const counts = {};
        clients.forEach(c => { counts[c.status] = (counts[c.status] || 0) + 1; });
        return counts;
    }, [clients]);
    // "Recently completed" chip (Jaylen 2026-09-21: same tab as the
    // walkthrough app, on the desktop list) — walkthroughs marked Complete
    // in the last RECENT_WT_DAYS days, newest first (clientOrder.js).
    const RECENT_KEY = 'recent_wt';
    const recentDone = useMemo(() => recentlyCompletedWalkthroughs(clients), [clients]);
    const recentMode = statusFilter === RECENT_KEY;
    // Header summary line (page-headers.md): live counts, not prose.
    const summary = useMemo(() => {
        // Same bucket the list's "Today's Walkthroughs" group uses (dated
        // today OR in progress), so the number matches the header row.
        const wtToday = (groupClientsForOrder(clients, jobs).find(g => g.key === 'today')?.items || []).length;
        const newLeads = statusCounts.new_lead || 0;
        return `${clients.length} client${clients.length === 1 ? '' : 's'} · ${newLeads} new lead${newLeads === 1 ? '' : 's'} · ${wtToday} walkthrough${wtToday === 1 ? '' : 's'} today`;
    }, [clients, jobs, statusCounts]);

    useEffect(() => {
        if (openClientId) {
            const client = clients.find(c => c.id === openClientId);
            if (client) setModal(client);
            clearOpenClient();
        }
    }, [openClientId, clients, clearOpenClient]);

    // Dashboard "New Client" → intake form in one click (nav flag; replaced
    // the fake-click-on-a-timer race, 2026-07-02).
    useEffect(() => {
        if (openNewClient) {
            setIntakeDate(newClientDate || null);
            setModal('new');
            clearOpenNewClient();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openNewClient]);

    // opts.silent — autosave from the client file (feedback.md 5C): no
    // toast per keystroke-batch; the file's status line says "Saved".
    const handleSave = async (form, opts = {}) => {
        const saved = await saveClient(form);
        if (!opts.silent) showToast(form.id ? 'Client updated' : 'Client added');
        if (!form.id && saved) setModal(null);
        return saved;
    };

    const q = search.toLowerCase();
    // Phone queries: match on digits only, so "(828) 844-4873", "828-844-4873"
    // and "8288444873" all hit the same normalized-stored number. Gate on >=3
    // digits so short text searches don't spuriously match every phone.
    const qDigits = phoneDigits(search);
    const filtered = clients.filter(c => {
        const phoneHit = qDigits.length >= 3 && (
            phoneDigits(c.phone).includes(qDigits) ||
            phoneDigits(c.secondary_phone).includes(qDigits) ||
            (Array.isArray(c.phones) && c.phones.some(p => phoneDigits(p?.number).includes(qDigits)))
        );
        const matchesSearch = (c.name || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q) ||
            phoneHit ||
            // Secondary contact (e.g. spouse) — searching their name/number
            // surfaces the primary client's file.
            (c.secondary_name || '').toLowerCase().includes(q) ||
            (c.secondary_email || '').toLowerCase().includes(q) ||
            (c.origin_address || '').toLowerCase().includes(q) ||
            (c.destination_address || '').toLowerCase().includes(q);
        const matchesStatus = statusFilter === 'all' || recentMode || c.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // "Smart" (default) = the unified ordering shared with the estimator
    // app (src/lib/clientOrder.js): Today's walkthroughs → Upcoming
    // walkthroughs → Moves coming up → Needs attention → Completed tail,
    // rendered with group header rows. Searching (or any other sort)
    // collapses to a flat list — when hunting one person, groups are noise.
    const sorted = (() => {
        if (recentMode) {
            // One group, completion order, each row tagged with its stamp
            // ("Done Sep 18 4:14 PM · by Jaylen"); search still narrows it.
            const ids = new Set(filtered.map(c => c.id));
            const items = recentDone.filter(c => ids.has(c.id)).map(c => ({ ...c, __wtDone: completedStamp(c) }));
            return items.length ? [{ __header: `Walkthroughs completed · last ${RECENT_WT_DAYS} days (${items.length})`, id: 'hdr-recent' }, ...items] : [];
        }
        if (sortBy === 'smart' && !search.trim()) {
            return groupClientsForOrder(filtered, jobs).flatMap(g => [
                { __header: `${g.label} (${g.items.length})`, id: `hdr-${g.key}` },
                // Completed-today walkthroughs stay in the today group all
                // day (Jaylen 2026-08-06) — tag them so the row dims + gets
                // a ✓ chip without touching other groups' rendering.
                ...(g.key === 'today' ? g.items.map(c => c.walkthrough_status === 'completed' ? { ...c, __wtDoneToday: true } : c) : g.items),
            ]);
        }
        return [...filtered].sort((a, b) => {
            if (sortBy === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '');
            if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
            if (sortBy === 'move_date') return (a.move_date || '9999-99-99').localeCompare(b.move_date || '9999-99-99');
            if (sortBy === 'wt_date') return (a.walkthrough_date || '9999-99-99').localeCompare(b.walkthrough_date || '9999-99-99');
            // 'newest' + smart-while-searching both land here
            return (b.created_at || '').localeCompare(a.created_at || '');
        });
    })();

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Clients"
                summary={summary}
                help={<>
                    <p>Every person who calls in lives here, from first contact to finished move. <span className="font-semibold">New Client</span> opens the intake form — capture name, phone, addresses, move date, and walkthrough details in one pass.</p>
                    <p>The colored pill is the client's <span className="font-semibold">pipeline status</span>: <span className="text-blue-600 font-semibold">New Lead</span> (just came in) → <span className="text-amber-600 font-semibold">Working</span> (actively quoting/scheduling) → <span className="text-slate-500 font-semibold">Inactive</span> (lead went cold). The flag next to it is <span className="font-semibold">priority</span> — it drives the Dashboard's follow-up queues.</p>
                    <p>Click a client to open their full file: details, estimates, photos, and notes all in one place. A "Date TBD" badge means their move date is <span className="font-semibold">tentative</span> — booking will ask you to confirm the date first.</p>
                    <p><span className="font-semibold">Ordering:</span> the default "Smart" sort groups by what's next — today's walkthroughs (in appointment order), upcoming walkthroughs, moves by date, leads needing attention, then completed walkthroughs. Walkthroughs finished today stay in the today group until midnight — dimmed with a ✓ so the list matches the dispatch board all day. The estimator app uses the exact same order. The dropdown switches to plain date/name sorts.</p>
                    <p><span className="font-semibold">Recent Walkthroughs</span> (the green chip) lists every walkthrough marked Complete in the last {RECENT_WT_DAYS} days, newest first, wherever Smart order would file the client — the fastest way back to the one you just finished.</p>
                    <p className="text-xs text-slate-400">Search matches names, phone numbers, and addresses.</p>
                </>}
                actions={
                    <Button variant="primary" leftIcon={<IconPlus size={18}/>} onClick={() => setModal('new')}>
                        New Client
                    </Button>
                }
            />

            {/* Search + status chips + sort — one toolbar card (page-headers.md). */}
            <Toolbar>
                <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, phone, or address..." />
                <FilterChips
                    ariaLabel="Filter by client status"
                    items={[
                        { key: 'all', label: 'All', count: clients.length },
                        ...statuses.map(stage => ({ key: stage.key, label: stage.label, count: statusCounts[stage.key] || 0, color: stage.color, bg: stage.bg })),
                        { key: RECENT_KEY, label: 'Recent Walkthroughs', count: recentDone.length, color: '#059669', bg: '#d1fae5' },
                    ]}
                    value={statusFilter}
                    onChange={(k) => setStatusFilter(k === statusFilter && k !== 'all' ? 'all' : k)}
                />
                <ChoiceKeys
                    size="sm"
                    ariaLabel="Sort clients by"
                    className="items-center ml-auto"
                    items={[
                        { value: 'smart', label: "Smart (what's next)" },
                        { value: 'move_date', label: 'Move Date' },
                        { value: 'wt_date', label: 'Walkthrough Date' },
                        { value: 'newest', label: 'Newest First' },
                        { value: 'oldest', label: 'Oldest First' },
                        { value: 'name', label: 'Name A–Z' },
                    ]}
                    value={sortBy}
                    onChange={v => setSortBy(v)}
                />
            </Toolbar>

            {viewMode === 'list' && sorted.length > 0 && (
                /* Ruled list — one panel, hairline rows (lists.md §2). */
                <div className={listPanelCls}>
                    {sorted.map(client => {
                        // Smart-sort group header pseudo-rows (listGroupCls bands inside the panel).
                        if (client.__header) {
                            return <div key={client.id} className={listGroupCls}>{client.__header}</div>;
                        }
                        const clientJobsList = jobs.filter(j => j.client_id === client.id);
                        const jobCount = clientJobsList.length;
                        // Move date via the current move (tentative flag rides
                        // along); the mirror column is the fallback for rows
                        // that predate the moves table.
                        const curMove = currentMoveFor(client, moves);
                        // Multi-move rows (showroom pick A, Jaylen 2026-09-18): one
                        // chip per LIVE move replaces the single type · date line
                        // when the client has more than one; the current move is
                        // the dark chip, tap another to open the file on it.
                        // Completed / cancelled moves stay out of the row.
                        const rowMoves = movesForClient(moves, client.id)
                            .filter(m => !MOVE_TERMINAL.has(m.status) || m.id === curMove?.id)
                            .sort((a, b) => (a.load_date || '9999').localeCompare(b.load_date || '9999') || (a.created_at || '').localeCompare(b.created_at || ''));
                        const multiMove = rowMoves.length > 1;
                        const openOnMove = async (m) => {
                            if (m.id !== client.current_move_id) { try { await setCurrentMove(client.id, m.id); } catch (e) { showToast('Couldn\'t switch move: ' + e.message, 'error'); } }
                            setModal(client);
                        };
                        const rowMoveDate = curMove ? curMove.load_date : client.move_date;
                        const rowTentative = curMove ? !!curMove.load_tentative : !!client.date_tentative;
                        const moveBadge = moveDateBadge(rowMoveDate);
                        const currentStatus = statuses.find(s => s.key === client.status) || statuses[0] || { color: '#94a3b8', label: client.status };

                        // Compute pipeline status — estimate status or operations status depending on stage
                        // The row's estimate is the CURRENT MOVE's live option
                        // (booked first), not simply the newest row — after a
                        // booking the newest sibling is the auto-declined one
                        // (found 2026-09-18 E2E). Same picker as the pipeline.
                        const primaryEst = pickPrimaryEstimate(estimates, client.id, curMove);
                        const primaryJob = clientJobsList.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null;
                        // Swap to operations once the estimate is booked AND a job exists
                        const inOperationsPhase = primaryEst && primaryEst.status === 'booked' && primaryJob;
                        const pipelineMode = inOperationsPhase ? 'operations' : (primaryEst ? 'estimate' : null);
                        const pipelineValue = inOperationsPhase ? (primaryJob.status || 'unassigned') : (primaryEst ? primaryEst.status : null);
                        const pipelineStatuses = pipelineMode === 'operations' ? operationsStatuses : estimateStatuses;

                        // handlePipelineChange removed 2026-05-14 — Pipeline status
                        // is now display-only on this page. Edits happen on the
                        // Estimates page (estimate status) or Dispatch board
                        // (operations status). See the pipeline-block comment below.

                        return (
                            <div key={client.id} data-testid="client-row" data-client-id={client.id}
                                role="button"
                                tabIndex={0}
                                aria-label={`Open ${client.name}'s client file`}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModal(client); } }}
                                className={`${listRowCls} ${modal && modal !== 'new' && modal.id === client.id ? listRowSelectedCls : ''} focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500/50 group relative ${client.__wtDoneToday ? 'opacity-60' : ''}`} onClick={() => setModal(client)}>
                                <div className="flex items-stretch min-h-[80px]">
                                    {/* Layout per Jaylen's 2026-05-14 mockup:
                                          [Name+badges] [Pipeline BIG block] [Phone] [From/To] [Client BIG block]
                                        Both status blocks render at full row height with the
                                        same saturated treatment so they read as headline UI
                                        when scanning leads. No header label above each — the
                                        block IS the indicator. */}

                                    {/* Name + badges — left column. The AgingChip was here
                                        briefly but the terse-label format ("RESPOND · 3D")
                                        wasn't legible at-a-glance; pulled back out 2026-05-14.
                                        Pipeline aging surfaces on the dashboard instead,
                                        where there's room to label everything clearly. */}
                                    <div className="flex-shrink-0 w-[240px] flex items-center px-4 min-w-0">
                                        <div className="min-w-0">
                                            <h3 className="text-base font-extrabold text-slate-900 truncate tracking-tight">{client.name}</h3>
                                            {multiMove ? (
                                                <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                    {client.__wtDoneToday && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide whitespace-nowrap">{'✓'} Walkthrough Done</span>}
                                                    {client.__wtDone && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 whitespace-nowrap">{'✓'} {client.__wtDone}</span>}
                                                    {rowMoves.map(m => {
                                                        const cur = m.id === curMove?.id;
                                                        const dateTxt = m.load_date ? fmtMoveDate(m.load_date, { tentative: m.load_tentative }) : 'no date';
                                                        return (
                                                            <button key={m.id} type="button"
                                                                onClick={(e) => { e.stopPropagation(); openOnMove(m); }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                title={cur ? 'Current move — the row reads from this one' : 'Open the file on this move'}
                                                                className={`inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full border text-[10.5px] font-bold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 ${cur ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-500'}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${cur ? 'bg-blue-400' : 'bg-slate-400'}`} aria-hidden="true" />
                                                                {jobTypeShort(m.job_type)} · <span className={m.load_tentative ? (cur ? 'text-amber-300' : 'text-amber-700') : ''}>{dateTxt}</span>
                                                            </button>
                                                        );
                                                    })}
                                                    {jobCount > 0 && <span className="text-[10px] text-slate-400 font-semibold self-center">{jobCount} job{jobCount > 1 ? 's' : ''}</span>}
                                                    {rowMoveDate && moveBadge && <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${moveBadge.cls}`}>{moveBadge.text}</span>}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                    {client.__wtDoneToday && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide whitespace-nowrap">{'✓'} Walkthrough Done</span>}
                                                    {client.__wtDone && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 whitespace-nowrap">{'✓'} {client.__wtDone}</span>}
                                                    {client.job_type && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-200 text-slate-700 uppercase tracking-wide">{client.job_type.replace('_',' ')}</span>}
                                                    {jobCount > 0 && <span className="text-[10px] text-slate-400 font-semibold">{jobCount} job{jobCount > 1 ? 's' : ''}</span>}
                                                    {rowMoveDate && <span className={`text-[10px] font-bold whitespace-nowrap ${rowTentative ? 'text-amber-700' : 'text-slate-500'}`} title={rowTentative ? 'Tentative move date' : 'Move date'}>{fmtMoveDate(rowMoveDate, { tentative: rowTentative })}</span>}
                                                    {rowMoveDate && moveBadge && <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${moveBadge.cls}`}>{moveBadge.text}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Pipeline status (Estimate or Operations) — DISPLAY-ONLY
                                        (per Jaylen 2026-05-14). Edits happen in the canonical
                                        domain pages: estimate status on the Estimates page,
                                        operations status on the Dispatch board. Keeping this
                                        block read-only avoids the mode-confusion footgun where
                                        the block silently flips between two different lifecycle
                                        contexts depending on stage. No chevron — it's not
                                        clickable. Clicks bubble up to open the client modal
                                        like the rest of the row. */}
                                    <div className="flex-shrink-0 w-[150px] relative">
                                        {(() => {
                                            const pipelineLabel = pipelineMode === 'operations' ? 'Ops Status' : 'Estimate Status';
                                            if (!pipelineMode) {
                                                // No estimate yet — slate placeholder.
                                                return (
                                                    <div className="w-full h-full flex flex-col overflow-hidden">
                                                        <div className="flex-shrink-0 pt-1.5 pb-1 px-3 flex items-center justify-center bg-white">
                                                            <span className="font-lemonmilk text-[10px] uppercase leading-none whitespace-nowrap text-slate-900" style={{ letterSpacing: '0.08em' }}>Estimate Status</span>
                                                        </div>
                                                        <div className="flex-1 flex items-center justify-center bg-slate-300 px-2 min-w-0">
                                                            <span className="font-lemonmilk text-base uppercase text-slate-500 leading-none whitespace-nowrap truncate">No estimate</span>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            const opt = pipelineStatuses.find(s => s.key === pipelineValue) || { color: '#94a3b8', label: pipelineValue };
                                            return (
                                                <div className="w-full h-full flex flex-col overflow-hidden">
                                                    <div className="flex-shrink-0 pt-1.5 pb-1 px-3 flex items-center justify-center bg-white">
                                                        <span className="font-lemonmilk text-[10px] uppercase leading-none whitespace-nowrap text-slate-900" style={{ letterSpacing: '0.08em' }}>
                                                            {pipelineLabel}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 flex items-center justify-center px-2 min-w-0"
                                                        style={{ backgroundColor: opt.color, color: statusTextColor(opt.color) }}>
                                                        <span className="font-lemonmilk text-base uppercase whitespace-nowrap leading-none truncate">
                                                            {opt.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* Priority block — uses the shared PriorityPicker
                                        component. Reads client.priority (sql/049). For
                                        admins/owners it's clickable and opens a portal
                                        dropdown; non-admins see a display-only block.
                                        Changing an already-set priority pops a confirmation
                                        ("Change [client]'s priority from X to Y?"). */}
                                    <div className="flex-shrink-0 w-[110px] relative" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                                        <PriorityPicker
                                            client={client}
                                            editable={canEditClient}
                                            variant="clients-row"
                                            onChanged={() => showToast(`${client.name} priority updated`)}
                                        />
                                    </div>

                                    {/* Phone + From/To — center column, takes remaining space */}
                                    <div className="flex-1 flex items-center gap-6 px-4 min-w-0">
                                        <div className="flex-shrink-0 text-sm font-semibold text-slate-800 truncate w-[120px]">{formatPhone(client.phone) || <span className="text-slate-300 font-normal">—</span>}</div>
                                        <div className="flex-1 text-xs space-y-0.5 min-w-0">
                                            {client.origin_address ? (
                                                <div className="flex items-center gap-1.5 text-slate-700 truncate"><span className="text-[9px] font-bold text-slate-400 w-7 flex-shrink-0">FROM</span><span className="truncate font-medium">{client.origin_address}</span></div>
                                            ) : <div className="text-slate-300 text-[10px]">No origin</div>}
                                            {client.destination_address ? (
                                                <div className="flex items-center gap-1.5 text-slate-700 truncate"><span className="text-[9px] font-bold text-slate-400 w-7 flex-shrink-0">TO</span><span className="truncate font-medium">{client.destination_address}</span></div>
                                            ) : <div className="text-slate-300 text-[10px]">No destination</div>}
                                        </div>
                                    </div>

                                    {/* Client status — full-height split block on the right
                                        edge. Same two-band layout as the pipeline block:
                                        lightened "CLIENT STATUS" header band over the full-
                                        saturated substatus band. */}
                                    <div className="flex-shrink-0 w-[150px] relative" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                                        <StatusDropdown value={client.status} statuses={statuses} size="block" blockRounding="rounded-none"
                                            blockLabel="Client Status"
                                            onChange={async (newStatus) => { if (newStatus !== client.status) { await saveClient({ ...client, status: newStatus }); showToast(`${client.name} → ${getStatusLabel(statuses, newStatus)}`); } }} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {sorted.length === 0 && (
                clients.length === 0 ? (
                    <EmptyState
                        icon={<IconUsers size={28}/>}
                        title="No clients yet"
                        hint="Every lead starts as a client file — add the first one and the walkthrough, estimate and move follow from it."
                        action={<Button size="sm" leftIcon={<IconPlus size={16}/>} onClick={() => setModal('new')}>New Client</Button>}
                    />
                ) : (
                    <EmptyState
                        icon={<IconUsers size={28}/>}
                        title={recentMode && !search.trim() ? 'Nothing completed recently' : 'No clients match'}
                        hint={recentMode && !search.trim() ? `Walkthroughs marked Complete in the last ${RECENT_WT_DAYS} days show up here.` : 'Nothing matches this search and status filter. Clear them to see the full list.'}
                        action={<Button variant="tertiary" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear filters</Button>}
                    />
                )
            )}

            {/* Slide-over workspace (client-file.md). `key` remounts the
                file when the header ‹ › flips to a neighbour so the form
                re-seeds from that client; neighbours follow the list as
                currently filtered + sorted. */}
            {modal && (() => {
                const rows = modal === 'new' ? [] : sorted.filter(c => !c.__header);
                const idx = modal === 'new' ? -1 : rows.findIndex(c => c.id === modal.id);
                const neighbors = idx >= 0 ? { prev: rows[idx - 1] || null, next: rows[idx + 1] || null } : null;
                return (
                    <ClientIntakeModal
                        key={modal === 'new' ? 'new' : modal.id}
                        client={modal === 'new' ? null : modal}
                        initialMoveDate={modal === 'new' ? intakeDate : null}
                        onClose={() => { setModal(null); setIntakeDate(null); }}
                        onSave={handleSave}
                        initialViewMode={modal !== 'new'}
                        jobs={jobs} crew={crew} trucks={trucks}
                        neighbors={neighbors}
                        onSwitch={(c) => setModal(clients.find(x => x.id === c.id) || c)}
                    />
                );
            })()}
        </div>
    );
}

export default ClientsPage;
